import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
// import { TempestAudio } from '@thetempest/sounds'; // TODO: Uncomment when sound files are added
import streamService from '../services/streams';
import listenHistoryService from '../services/listenHistoryService';
import { logger } from '../utils/logger';
import { rewriteAudioUrl } from '../utils/audioUrl';
import { applyDrift, resetDrift, driftHueForStream } from '../utils/drift';
import Hls from 'hls.js';
import { attachSource, detachHls, CurrentQuality } from '../utils/hlsAttach';
import { useErrorToast } from './ErrorToastContext';

/**
 * Map an HTMLAudioElement MediaError code to user-visible copy. See
 * TEM-870: during the 2026-05-24 archive outage the symptom was
 * 'press play → button stops spinning → nothing happens', which cost
 * us hours of misdiagnosis. Users get a signal now, even when the
 * server-side TEM-868 hardening hasn't quite reached the failure
 * (CORS, codec, network blip, etc.).
 */
export function mapMediaErrorToCopy(code: number | undefined): string {
  // HTMLAudioElement.error.code values per HTML5 spec
  switch (code) {
    case 1: // MEDIA_ERR_ABORTED
      return 'Playback was canceled. Tap retry to try again.';
    case 2: // MEDIA_ERR_NETWORK
      return 'This stream is temporarily unavailable. Try again in a few minutes.';
    case 3: // MEDIA_ERR_DECODE
      return 'This audio file looks corrupted.';
    case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED — also fires for 403/404 source URLs
      return 'This stream is temporarily unavailable. Try again in a few minutes.';
    default:
      return 'Playback failed. Tap retry.';
  }
}

export interface Stream {
  streamId: string;
  name: string;
  description?: string;
  audioUrl: string;
  hlsUrl?: string;
  format?: string;
  coverImage?: string;
  uploaderUsername?: string;
  uploaderDisplayName?: string;
  waveformData?: number[];
}

interface AudioPlayerContextType {
  currentStream: Stream | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  currentQuality: CurrentQuality;
  // Queue state
  queue: Stream[];
  queueIndex: number;
  // Playback controls
  playStream: (stream: Stream) => void;
  togglePlayPause: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  clearStream: () => void;
  // Queue controls
  addToQueue: (stream: Stream) => void;
  removeFromQueue: (index: number) => void;
  clearQueue: () => void;
  playNext: () => void;
  playPrevious: () => void;
  skipTo: (index: number) => void;
  reorderQueue: (fromIndex: number, toIndex: number) => void;
  hasNext: boolean;
  hasPrevious: boolean;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentStream, setCurrentStream] = useState<Stream | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [queue, setQueue] = useState<Stream[]>([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [currentQuality, setCurrentQuality] = useState<CurrentQuality>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const playCountTrackedRef = useRef<string | null>(null); // Track which stream's play count has been incremented
  const playCountTrackedSet = useRef<Set<string>>(new Set()); // Track all streams whose play count was incremented

  // Refs for auto-advance (to avoid stale closures in event handler)
  const queueRef = useRef<Stream[]>([]);
  const queueIndexRef = useRef(-1);

  // TEM-870 — surface playback failures to the user via toast.
  const { showToast } = useErrorToast();
  // Ref-stash so the once-only setup useEffect can call the latest hook
  // implementation without being re-bound. (showToast is stable due to
  // useCallback in ErrorToastContext, but the ref keeps the contract
  // explicit for future readers.)
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const currentStreamRef = useRef<Stream | null>(null);
  currentStreamRef.current = currentStream;
  // I1 (TEM-1058): the last LOGICAL url handed to attachSource. Under hls.js,
  // audio.src becomes a blob: MSE URL, so it can't be compared against the
  // stream's real url to detect a same-stream re-click. Track the logical url
  // here and compare against it instead. Set at every attachSource call site.
  const lastAttachedUrlRef = useRef<string | null>(null);

  // Keep refs in sync with state
  queueRef.current = queue;
  queueIndexRef.current = queueIndex;

  // Initialize audio element
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    // Preload UI sounds
    // TODO: Add sound files to public/sounds/ directory
    // const preloadSounds = async () => {
    //   try {
    //     await TempestAudio.preload('click', 'water-drop.mp3');
    //   } catch (error) {
    //     logger.info('Sound files not yet added to public/sounds/');
    //   }
    // };
    // preloadSounds();

    // Event listeners
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Auto-advance to next track in queue using refs
      const currentQueue = queueRef.current;
      const currentIndex = queueIndexRef.current;
      if (currentQueue.length > 0 && currentIndex < currentQueue.length - 1) {
        const nextIndex = currentIndex + 1;
        const nextStream = currentQueue[nextIndex];
        // Play next track directly
        setCurrentStream(nextStream);
        setQueueIndex(nextIndex);
        // Cinematic drift: ease the whole frame to the next stream's accent.
        applyDrift(driftHueForStream(nextStream.streamId));
        lastAttachedUrlRef.current = rewriteAudioUrl(nextStream.hlsUrl ?? nextStream.audioUrl);
        attachSource(audio, hlsRef, nextStream, { onLevel: setCurrentQuality });
        audio.play().catch(err => logger.error('Auto-advance play failed:', err));
        // Track play count and listen history
        if (!playCountTrackedSet.current.has(nextStream.streamId)) {
          playCountTrackedSet.current.add(nextStream.streamId);
          streamService.incrementPlayCount(nextStream.streamId).catch(err => {
            logger.error('Failed to increment play count:', err);
          });
          listenHistoryService.recordListen(nextStream.streamId, nextStream.name, {
            coverImage: nextStream.coverImage,
            audioUrl: nextStream.audioUrl,
            uploaderUsername: nextStream.uploaderUsername,
            uploaderDisplayName: nextStream.uploaderDisplayName,
          }).catch(err => {
            logger.error('Failed to record listen history:', err);
          });
        }
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleError = (e: Event) => {
      logger.error('Audio playback error:', e);
      setIsPlaying(false);
      // TEM-870: dispatch a user-visible toast. Dedupe per stream-id
      // within 30s so a retry loop doesn't spam.
      const target = e.target as HTMLAudioElement | null;
      const errCode = target?.error?.code;
      const stream = currentStreamRef.current;
      const message = mapMediaErrorToCopy(errCode);
      try {
        showToastRef.current(message, {
          dedupeToken: stream?.streamId ?? 'no-stream',
          dedupeWindowMs: 30_000,
          action: stream
            ? {
                label: 'Retry',
                onClick: () => {
                  if (!audioRef.current || !stream) return;
                  lastAttachedUrlRef.current = rewriteAudioUrl(stream.hlsUrl ?? stream.audioUrl);
                  attachSource(audioRef.current, hlsRef, stream, { onLevel: setCurrentQuality });
                  audioRef.current.play().catch((err) =>
                    logger.error('Retry play failed:', err),
                  );
                },
              }
            : undefined,
        });
      } catch (toastErr) {
        // Defensive: if the ErrorToastProvider isn't mounted (e.g. in a
        // test that uses AudioPlayerProvider directly), don't crash the
        // event handler.
        logger.error('Failed to show error toast:', toastErr);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      detachHls(hlsRef);
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Helper to play a stream at a specific index (internal use)
  const playStreamAtIndex = (stream: Stream, index: number) => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    setCurrentStream(stream);
    setQueueIndex(index);
    // Cinematic drift: ease --drift/--glow to this stream's accent.
    applyDrift(driftHueForStream(stream.streamId));
    lastAttachedUrlRef.current = rewriteAudioUrl(stream.hlsUrl ?? stream.audioUrl);
    attachSource(audio, hlsRef, stream, { onLevel: setCurrentQuality });
    audio.volume = isMuted ? 0 : volume;
    audio.play().catch(err => logger.error('Play failed:', err));

    // Track play count for new stream (only once per session)
    if (!playCountTrackedSet.current.has(stream.streamId)) {
      playCountTrackedSet.current.add(stream.streamId);
      playCountTrackedRef.current = stream.streamId;
      streamService.incrementPlayCount(stream.streamId).catch(err => {
        logger.error('Failed to increment play count:', err);
      });
      // Record listen history
      listenHistoryService.recordListen(stream.streamId, stream.name, {
        coverImage: stream.coverImage,
        audioUrl: stream.audioUrl,
        uploaderUsername: stream.uploaderUsername,
        uploaderDisplayName: stream.uploaderDisplayName,
      }).catch(err => {
        logger.error('Failed to record listen history:', err);
      });
    }
  };

  const playStream = (stream: Stream) => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    // Same stream re-clicked: if the URL has changed (e.g. caller refetched
    // the stream and got a fresher signed/CloudFront URL), reload before
    // playing; otherwise just toggle.
    if (currentStream?.streamId === stream.streamId) {
      const desiredSrc = rewriteAudioUrl(stream.hlsUrl ?? stream.audioUrl);
      // Compare against the last LOGICAL url, not audio.src — under hls.js the
      // latter is a blob: MSE URL and would always mismatch, restarting the
      // stream on every re-click (I1).
      if (lastAttachedUrlRef.current !== desiredSrc) {
        lastAttachedUrlRef.current = desiredSrc;
        attachSource(audio, hlsRef, stream, { onLevel: setCurrentQuality });
        audio.play().catch(err => logger.error('Play failed:', err));
        return;
      }
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play().catch(err => logger.error('Play failed:', err));
      }
      return;
    }

    // Check if stream is already in queue
    const existingIndex = queue.findIndex(s => s.streamId === stream.streamId);
    if (existingIndex !== -1) {
      // Play from existing queue position
      playStreamAtIndex(stream, existingIndex);
    } else {
      // Add to queue and play
      const newQueue = [...queue, stream];
      const newIndex = newQueue.length - 1;
      setQueue(newQueue);
      playStreamAtIndex(stream, newIndex);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current || !currentStream) return;

    // TODO: Uncomment when sound files are added
    // TempestAudio.play('click');

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => logger.error('Play failed:', err));
    }
  };

  const seek = (time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const setVolume = (newVolume: number) => {
    if (!audioRef.current) return;
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    audioRef.current.volume = isMuted ? 0 : clampedVolume;
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    audioRef.current.volume = newMuted ? 0 : volume;
  };

  const clearStream = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    detachHls(hlsRef);
    setCurrentQuality(null);
    // Cinematic drift: return the frame to the neutral Storm-Blue seed.
    resetDrift();
    audioRef.current.src = '';
    setCurrentStream(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setQueue([]);
    setQueueIndex(-1);
    playCountTrackedRef.current = null;
    playCountTrackedSet.current.clear();
  };

  // Queue management functions
  const addToQueue = (stream: Stream) => {
    // Don't add duplicates
    if (queue.some(s => s.streamId === stream.streamId)) {
      logger.info('[AudioPlayer] Stream already in queue', { streamId: stream.streamId });
      return;
    }
    setQueue(prev => [...prev, stream]);
    // If nothing is currently playing, start playing this stream
    if (!currentStream) {
      playStreamAtIndex(stream, queue.length);
    }
  };

  const removeFromQueue = (index: number) => {
    if (index < 0 || index >= queue.length) return;

    const newQueue = [...queue];
    newQueue.splice(index, 1);
    setQueue(newQueue);

    // Adjust queueIndex if needed
    if (index < queueIndex) {
      setQueueIndex(prev => prev - 1);
    } else if (index === queueIndex) {
      // Removed current track
      if (newQueue.length === 0) {
        clearStream();
      } else if (index < newQueue.length) {
        // Play next track at same index
        playStreamAtIndex(newQueue[index], index);
      } else {
        // Was last track, play previous
        playStreamAtIndex(newQueue[newQueue.length - 1], newQueue.length - 1);
      }
    }
  };

  const clearQueue = () => {
    setQueue([]);
    setQueueIndex(-1);
    // Keep current stream playing but clear the queue
  };

  const playNext = () => {
    if (queue.length === 0 || queueIndex >= queue.length - 1) return;
    const nextIndex = queueIndex + 1;
    playStreamAtIndex(queue[nextIndex], nextIndex);
  };

  const playPrevious = () => {
    // If we're more than 3 seconds in, restart current track
    if (currentTime > 3 && audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    // Otherwise go to previous track
    if (queue.length === 0 || queueIndex <= 0) return;
    const prevIndex = queueIndex - 1;
    playStreamAtIndex(queue[prevIndex], prevIndex);
  };

  const skipTo = (index: number) => {
    if (index < 0 || index >= queue.length) return;
    playStreamAtIndex(queue[index], index);
  };

  const reorderQueue = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= queue.length) return;
    if (toIndex < 0 || toIndex >= queue.length) return;

    const newQueue = [...queue];
    const [removed] = newQueue.splice(fromIndex, 1);
    newQueue.splice(toIndex, 0, removed);
    setQueue(newQueue);

    // Update queueIndex if the current track was moved
    if (fromIndex === queueIndex) {
      setQueueIndex(toIndex);
    } else if (fromIndex < queueIndex && toIndex >= queueIndex) {
      setQueueIndex(prev => prev - 1);
    } else if (fromIndex > queueIndex && toIndex <= queueIndex) {
      setQueueIndex(prev => prev + 1);
    }
  };

  const hasNext = queue.length > 0 && queueIndex < queue.length - 1;
  const hasPrevious = queue.length > 0 && (queueIndex > 0 || currentTime > 3);

  const value: AudioPlayerContextType = {
    currentStream,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    currentQuality,
    queue,
    queueIndex,
    playStream,
    togglePlayPause,
    seek,
    setVolume,
    toggleMute,
    clearStream,
    addToQueue,
    removeFromQueue,
    clearQueue,
    playNext,
    playPrevious,
    skipTo,
    reorderQueue,
    hasNext,
    hasPrevious,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};
